import {useState, useRef, useEffect} from "react";

import "./OilFinder.css";
import "../search/SearchOverview.css";

import SearchResultCard from "../../listView/searchResults";
import {
    getOilFinderResults, getOilFinderModelandSheets, sendContact, ApiError,
} from "../../../utility/FetchAPI";

import {useTranslation} from "react-i18next";
import {Helmet} from "react-helmet-async";


import {WbIcon} from "@workbench/react";
import infoIcon from "../../../resource/icons/ic_bevo_info_48.svg";
import {emptyContactObject} from "../../../interfaces/IContact";

export const OilFinder = () => {
    const {t, i18n} = useTranslation();

    const [searchText, setSearchText] = useState("");
    const [oilFinderResults, setOilFinderResults] = useState(null);
    const [oilFinderError, setOilFinderError] = useState(null);
    const [oilSheetError, setOilSheetError] = useState(null);

    const [priorityEngineOil, setPriorityEngineOil] = useState(null);
    const [otherEngineOil, setOtherEngineOil] = useState(null);
    const [engineName, setEngineName] = useState(null);
    const [modelName, setModelName] = useState("");
    const resultsRef = useRef(null);

    const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);
    const [isLoadingOilSheets, setIsLoadingOilSheets] = useState(false);

    const [initialState, setInitialState] = useState(true);
    const [emailSent, setEmailSent] = useState(false);
    const [email, setEmail] = useState("");
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [errorEmail, setErrorEmail] = useState(false);
    const [errorTitle, setErrorTitle] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);

    useEffect(() => {
        if (oilFinderResults && priorityEngineOil && resultsRef.current) {
            requestAnimationFrame(() => {
                resultsRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            });
        }
    }, [oilFinderResults, priorityEngineOil]);

    const clearForm = () => {
        setInitialState(true);
        setEmail("");
        setTitle("");
        setMessage("");
        setErrorEmail(false);
        setErrorTitle(false);
        setErrorMessage(false);
    };

    const validateEmail = (value) => {
        return value
            .toLowerCase()
            .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    };

    const checkInput = () => {
        setErrorEmail(!validateEmail(email));
        setErrorTitle(!title);
        setErrorMessage(!message);
    };

    const addEmail = (value) => {
        setEmail(value);
        setErrorEmail(!validateEmail(value));
    };

    const addTitle = (value) => {
        setTitle(value);
        setErrorTitle(!value);
    };

    const addMessage = (value) => {
        setMessage(value);
        setErrorMessage(!value);
    };

    const sendMail = () => {
        checkInput();

        const hasEmailError = !validateEmail(email);
        const hasTitleError = !title;
        const hasMessageError = !message;

        if (hasEmailError || hasTitleError || hasMessageError) {
            setInitialState(false);
            return;
        }

        const contactData = {
            ...emptyContactObject, email, title, vin: searchText, message, locale: i18n.language,
        };

        sendContact(contactData)
            .then(() => {
                clearForm();
                setEmailSent(true);
            })
            .catch((error) => {
                console.error("Error sending contact mail:", error);
            });
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const showContactForm = () => {
        setEmailSent(false);
        clearForm();

        document?.getElementById("contactModalContainer")?.classList.remove("hide");
        document?.getElementById("contactFormTabContent")?.classList.add("wb-tab-content--selected");
        document?.getElementById("tabs")?.setAttribute("selected", "contactForm");
    };

    const closeModal = () => {
        document?.getElementById("contactModalContainer")?.classList.add("hide");
        document?.getElementById("contactFormTabContent")?.classList.remove("wb-tab-content--selected");
        document?.getElementById("tabs")?.setAttribute("selected", "");
    };

    const loadOilSheets = async (vehicle) => {
        const equipmentCodes = vehicle?.codeInfo?.equipmentCodes?.map((item) => item.code).filter(Boolean) || [];

        const engineId = vehicle?.majorAssembliesBaseInfo?.engine?.id;
        const vehicleModelDesignation = vehicle?.baseInfo?.vehicleModelDesignation;

        if (equipmentCodes.length === 0) {
            setOilSheetError({
                title: "No equipment codes found",
                detail: "The vehicle response did not contain any equipment codes.",
                status: 400,
            });
            return;
        }

        if (!engineId || !vehicleModelDesignation) {
            setOilSheetError({
                title: "Vehicle data incomplete", detail: "Required vehicle information is missing.", status: 400,
            });
            return;
        }

        try {
            setIsLoadingOilSheets(true);

            const oilResults = await getOilFinderModelandSheets(engineId.slice(0, 6), vehicleModelDesignation, equipmentCodes);

            console.log(oilResults);

            setPriorityEngineOil(oilResults?.data?.body?.prioritySheet || null);
            setOtherEngineOil(oilResults?.data?.body?.otherSheets || null);
            setModelName(oilResults?.data?.body?.vehicleModel || "");
            setEngineName(oilResults?.data?.body?.engineName || "");
            if (oilResults.data.body == null) {
                setOilSheetError(true);
            } else {
                setOilSheetError(null);
            }
        } catch (error) {
            console.error("Error fetching oil finder model and sheets:", error);
            setOilSheetError({
                title: "Oil data unavailable",
                detail: "Vehicle data was found, but the approved oil sheets could not be loaded.",
                status: error?.response?.status || 500,
            });
        } finally {
            setIsLoadingOilSheets(false);
        }
    };

    const searchButton = async () => {
        setOilFinderError(null);
        setOilSheetError(null);
        setOilFinderResults(null);
        setPriorityEngineOil(null);
        setOtherEngineOil(null);
        setEngineName(null);
        setModelName("");
        setEmailSent(false);

        try {
            setIsLoadingVehicle(true);

            const vehicle = await getOilFinderResults(searchText);

            if (!vehicle) {
                setOilFinderError({
                    title: "No data returned", detail: "The vehicle service returned no usable data.", status: 500,
                });
                return;
            }

            setOilFinderResults(vehicle);
            setOilFinderError(null);

            void loadOilSheets(vehicle);
        } catch (error) {
            console.error("Error fetching oil finder results:", error);

            if (error instanceof ApiError && error.problem) {
                setOilFinderError(error.problem);
            } else if (error instanceof Error) {
                setOilFinderError({
                    title: "Request failed",
                    detail: error.message || "Unexpected error while loading vehicle data.",
                    status: 500,
                });
            } else {
                setOilFinderError({
                    title: "Request failed", detail: "Unexpected error while loading vehicle data.", status: 500,
                });
            }

            setOilFinderResults(null);
        } finally {
            setIsLoadingVehicle(false);
        }
    };

    return (
        <div className="oilFinderContent">
            <Helmet>
                <title>{t("OilFinderSEOTitle", "Home | Mercedes-Benz Trucks Operating Fluids")}</title>
                <meta name="description" content={t("OilFinderPageDescription")}/>
            </Helmet>

            <div className="imageTitle">
                <span>Mercedes-Benz Trucks Operating Fluids:</span>
                <span>{t("OilFinder")}</span>
            </div>

            <div id="newSearchBox">
                <div id="upper">
                    <input
                        id="newSearch"
                        type="search"
                        value={searchText}
                        maxLength="17"
                        placeholder={t("VInNumber")}
                        onChange={handleSearchChange}
                    />
                    <label htmlFor="newSearch">{t("VInNumber")}</label>

                    <button type="button" onClick={searchButton} disabled={isLoadingVehicle}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                             fill="currentColor">
                            <path
                                d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                        </svg>
                        {isLoadingVehicle ? t("Loading", "Loading...") : t("Search")}
                    </button>

                    <div id="errorArea">
                        {oilFinderError && (<strong className="error">{t("VinErrorHelperText")}</strong>)}
                        {!oilFinderError && oilSheetError && (
                            <strong className="error">{t("EngineErrorHelperText")}</strong>)}
                    </div>

                    <div id="helpText">
                        <p><img src="/static/media/ic_bevo_info_48.5bb069740930d2d844bb518eae961d41.svg"
                                alt="Info"/>{t("OilFinderInfoText")}</p>
                        <p>{t("OilFinderHelperText")}</p>
                        <p>{t("OilFinderHelperActionText")}</p>
                    </div>
                </div>

                <div id="lower">
                    <div id="contact">
                        <p>{t("SubmitHelpText")}</p>
                        <button type="button" onClick={showContactForm}>
                            {t("SubmitHelp")}
                        </button>
                    </div>
                </div>
            </div>

            <div className="wb-grid-container" style={{marginTop: "80px", marginBottom: "200px"}}>
                {oilFinderResults && !oilSheetError && (<div style={{width: "100%"}}>
                    <div
                        style={{marginTop: "80px"}}
                        className="wb-type-heading-m text-white TopCategoryTitle description"
                    >
                        <h4
                            className="wb-type-heading-m text-white TopCategoryTitle description"
                            style={{marginTop: "30px", color: "white"}}
                        >
                            {modelName}
                        </h4>

                        <h1
                            className="wb-type-heading-m text-white TopCategoryTitle description firstColumnText"
                            style={{marginTop: "30px", fontSize: "-webkit-xxx-large"}}
                        >
                            {engineName}
                        </h1>

                        <h3
                            className="wb-type-heading-m text-white TopCategoryTitle description"
                            style={{marginTop: "30px", color: "white"}}
                        >
                            {t("EngineOil")} - {t("FactoryEngineOil")}:
                        </h3>
                    </div>

                    {isLoadingOilSheets && (<div style={{marginTop: "20px", color: "white"}}>
                        <p>{t("LoadingOilSheets", "Loading approved oil sheets...")}</p>
                    </div>)}

                    {priorityEngineOil && (<div className="cardContainer" ref={resultsRef}>
                        <SearchResultCard
                            key={0}
                            sheetNumber={priorityEngineOil}
                            prioritySheet={true}
                            t={t}
                            i18n={i18n}
                        />

                        {otherEngineOil && (<div className="additional-tab">
                            <h1
                                className="wb-type-heading-m TopCategoryTitle description"
                                style={{marginTop: "30px", color: "white"}}
                            >
                                {t("AdditionalEngineOil")}
                            </h1>

                            {otherEngineOil
                                .split(";")
                                .map((rawItem, index) => {
                                    const trimmedItem = rawItem?.trim();
                                    if (!trimmedItem) return null;

                                    return (<SearchResultCard
                                        key={index}
                                        sheetNumber={trimmedItem}
                                        prioritySheet={false}
                                        t={t}
                                        i18n={i18n}
                                    />);
                                })}
                        </div>)}
                    </div>)}

                    <p className="disclaimer">{t("disclaimerText")}</p>

                </div>)}
            </div>

            <div className="quickAnswers">
                <h2>{t("QuickAnswers")}</h2>
                <ul>
                    <li className="question">{t("OilFinderQa10")}</li>
                    <li className="answer">{t("OilFinderQa11")}</li>
                    <li className="question">{t("OilFinderQa20")}</li>
                    <li className="answer">{t("OilFinderQa21")}</li>
                    <li className="question">{t("OilFinderQa30")}</li>
                    <li className="answer">{t("OilFinderQa31")}</li>
                    <li className="question">{t("OilFinderQa40")}</li>
                    <li className="answer">{t("OilFinderQa41")}</li>
                </ul>
            </div>
            <div className="faq">
                <h2>{t("FrequentlyAskedQuestions")}</h2>
                <details name="question">
                    <summary>{t("OilFinderFaq10")}</summary>
                    <p>{t("OilFinderFaq11")}</p>
                </details>
                <details name="question">
                    <summary>{t("OilFinderFaq20")}</summary>
                    <p>{t("OilFinderFaq21")}</p>
                </details>
                <details name="question">
                    <summary>{t("OilFinderFaq30")}</summary>
                    <p>{t("OilFinderFaq31")}</p>
                </details>
                <details name="question">
                    <summary>{t("OilFinderFaq40")}</summary>
                    <p>{t("OilFinderFaq41")}</p>
                </details>
                <details name="question">
                    <summary>{t("OilFinderFaq50")}</summary>
                    <p>{t("OilFinderFaq51")}</p>
                </details>
                <details name="question">
                    <summary>{t("OilFinderFaq60")}</summary>
                    <p>{t("OilFinderFaq61")}</p>
                </details>
                <details name="question">
                    <summary>{t("OilFinderFaq70")}</summary>
                    <p>{t("OilFinderFaq71")}</p>
                </details>
                <details name="question">
                    <summary>{t("OilFinderFaq80")}</summary>
                    <p>{t("OilFinderFaq81")}</p>
                </details>
            </div>

            <div id="contactModalContainer" className="hide">
                <wb-tabs id="tabs">
                    <wb-tab-content id="contactFormTabContent" name="contactForm">
                        <div className="wb-grid-container wb-type-label formContainer">
                            <h2 className="wb-type-heading-l text-white TopCategoryTitle description">
                                {t("Contact")}
                            </h2>

                            {emailSent ? (
                                <div className="wb-grid-row rowStyle">
                                    <div className="wb-grid-col-mq1-12">
                                        <p className="wb-type-copy-primary text-white">
                                            {t("MailSendSuccess")}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="wb-grid-row rowStyle">
                                        {/*}<div className="wb-grid-col-mq1-2 contactLabels">{t("Email")}</div>{*/}
                                        <div className="wb-grid-col-mq1-12 mobileContact">
                                            <wb-input-control error={!initialState && errorEmail}>
                                                <wb-input>
                                                    <label>{t("Email")}</label>
                                                    <input
                                                        aria-labelledby="EmailError"
                                                        type="email"
                                                        value={email}
                                                        onChange={(e) => addEmail(e.target.value)}
                                                    />
                                                </wb-input>
                                            </wb-input-control>
                                            {!initialState && errorEmail && (
                                                <p className="wb-type-hint-strong error">{t("ErrorEmail")}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="wb-grid-row rowStyle">
                                        {/*}<div className="wb-grid-col-mq1-2 contactLabels">{t("Title")}</div>{*/}
                                        <div className="wb-grid-col-mq1-12 mobileContact">
                                            <wb-option-control error={!initialState && errorTitle}>
                                                <wb-option>
                                                    <select
                                                        name="supportSubject"
                                                        value={title}
                                                        onChange={(e) => addTitle(e.target.value)}
                                                    >
                                                        <option value="" disabled>
                                                            {t("SupportDisplayOption0")}
                                                        </option>
                                                        <option value={t("SupportSubjectOption1")}>
                                                            {t("SupportDisplayOption1")}
                                                        </option>
                                                        <option value={t("SupportSubjectOption2")}>
                                                            {t("SupportDisplayOption2")}
                                                        </option>
                                                        <option value={t("SupportSubjectOption3")}>
                                                            {t("SupportDisplayOption3")}
                                                        </option>
                                                        <option value={t("SupportSubjectOption4")}>
                                                            {t("SupportDisplayOption4")}
                                                        </option>
                                                    </select>
                                                </wb-option>
                                            </wb-option-control>
                                            {!initialState && errorTitle && (
                                                <p className="wb-type-hint-strong error">{t("ErrorTitle")}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="wb-grid-row rowStyle">
                                        {/*}<div className="wb-grid-col-mq1-2 contactLabels">{t("vin")}</div>{*/}
                                        <div className="wb-grid-col-mq1-12 mobileContact">
                                            <wb-input-control>
                                                <wb-input>
                                                    <label>{t("vin")}</label>
                                                    <input
                                                        id="VIN"
                                                        aria-labelledby="VIN"
                                                        value={searchText}
                                                        readOnly={true}
                                                    />
                                                </wb-input>
                                            </wb-input-control>
                                        </div>
                                    </div>

                                    <div className="wb-grid-row rowStyle">
                                        {/*}<div className="wb-grid-col-mq1-2 contactLabels">{t("Message")}</div>{*/}
                                        <div className="wb-grid-col-mq1-12 mobileContact">
                                            <wb-input-control error={!initialState && errorMessage}>
                                                <wb-input>
                                                    <label>{t("Message")}</label>
                                                    <textarea
                                                        aria-labelledby="MessageError"
                                                        value={message}
                                                        onChange={(e) => addMessage(e.target.value)}
                                                    />
                                                </wb-input>
                                            </wb-input-control>
                                            {!initialState && errorMessage && (
                                                <p className="wb-type-hint-strong error">{t("ErrorMessage")}</p>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="wb-grid-row rowStyle" style={{float: "right"}}>
                                <div
                                    className="wb-grid-col-mq1-12 wb-grid-col-mq3-4 wb-grid-col-mq4-2 mobileContact sendMailBtnStyle">
                                    <button
                                        type="button"
                                        className="wb-button wb-button--secondary wb-button--large contactButton"
                                        onClick={closeModal}
                                        aria-label="Close modal"
                                    >
                                        {t("Close")}
                                    </button>

                                    <button
                                        type="button"
                                        className="wb-button wb-button--primary wb-button--large contactButton"
                                        onClick={sendMail}
                                        disabled={emailSent}
                                    >
                                        {t("Send")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </wb-tab-content>
                </wb-tabs>
            </div>
        </div>);
};